import Clinic from "models/clinic";
import React from "react";
import styles from "./next-available-tag.module.css";
import { ReactComponent as GreenEllipse } from "../../../assets/images/ellipse-green.svg";
import { ReactComponent as GrayEllipse } from "../../../assets/images/ellipse-gray.svg";
import { DateTime } from "luxon";
import { UserLanguage } from "utils/types";
import useLanguage from "hooks/language";

export default function NextAvailableTag({
    nextAvailableTime,
    language,
}: {
    nextAvailableTime: Clinic["nextAvailableTime"];
    language: UserLanguage;
}): React.ReactElement {
    const { translations } = useLanguage();
    const nextAvailableDate = nextAvailableTime
        ? DateTime.fromISO(nextAvailableTime)
        : null;
    const sevenDaysFromNow = DateTime.now().plus({ days: 7 });
    const nextAvailabilityTranslation =
        translations.humanizeDateFormatter.nextAvailability;
    const formattedAvailability = formatAvailability(
        nextAvailableDate,
        language,
        nextAvailabilityTranslation
    );

    const shouldDisplayAvailabilityWithin7DaysTag =
        !!nextAvailableDate && nextAvailableDate < sevenDaysFromNow;

    return (
        <span
            className={`${styles.nextAvailableTag} ${
                shouldDisplayAvailabilityWithin7DaysTag
                    ? styles.withinSevenDaysWindowTag
                    : styles.outsideSevenDaysWindowTag
            }`}
        >
            {shouldDisplayAvailabilityWithin7DaysTag ? (
                <GreenEllipse />
            ) : (
                <GrayEllipse />
            )}
            {formattedAvailability}
        </span>
    );
}

export function ResultsIn48HoursTag({
    text,
}: {
    text: string;
}): React.ReactElement {
    return (
        <span
            className={`${styles.nextAvailableTag} ${styles.resultsIn48HoursTag}`}
        >
            &#9889; {text}
        </span>
    );
}

function formatAvailability(
    date: DateTime | null,
    language: UserLanguage,
    nextAvailabilityTranslation: string
): string | null {
    if (!date) return null;

    const now = DateTime.now().setLocale(language).startOf("day");
    const targetDay = date.setLocale(language).startOf("day");

    const diffInDays = Math.floor(targetDay.diff(now, "days").days);

    const time = date.setLocale(language).toFormat("H'h'mm");

    if (diffInDays === 0 || diffInDays === 1) {
        const rtf = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
        const relativeDay = rtf.format(diffInDays, "day");

        return `${nextAvailabilityTranslation} ${relativeDay} ${time}`;
    }

    let weekday = date.setLocale(language).toFormat("cccc");
    let month = date.setLocale(language).toFormat("MMM");
    if (language === "en") {
        weekday = capitalize(weekday);
        month = capitalize(month);
    }

    if (diffInDays > 1 && diffInDays < 7) {
        return `${nextAvailabilityTranslation} ${weekday} ${time}`;
    }

    return `${nextAvailabilityTranslation} ${weekday} ${date.day} ${month} ${time}`;
}

const capitalize = (str: string) =>
    str.length ? str.charAt(0).toUpperCase() + str.slice(1) : str;
